// src/sim/ai/pathing.ts — A* path request throttle + cache + sharing (D-08).
//
// Pitfall 13: pathfinding spam avoidance.
//   - Per-agent throttle: ≤1 new A* compute per agent per P4_PATH_AGENT_THROTTLE_TICKS (15 ticks = 500ms).
//   - Goal-tile-keyed shared cache with P4_PATH_CACHE_TTL_TICKS (60 ticks ≈ 2s) TTL.
//   - Path sharing: if another agent already computed a path to the same goal tile within TTL,
//     we trim that cached path to start from the requesting agent's position.
//   - Per-tick global budget: P4_PATHS_PER_TICK_BUDGET limits fresh A* computes per tick.
//
// Threat T-04-07: resetPathingForTick prunes stale cache entries (DoS guard).
// Threat T-04-08: per-tick budget returns 'stale-throttled' when exceeded (DoS guard).
//
// MANDATORY: uses findTilePath from src/sim/map/pathfinding.ts which does PF.Grid.clone()
// per call (Pitfall 1 / P1 learning). Do NOT clone here; findTilePath owns that.
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-08; 04-RESEARCH.md Pattern 6 L628-705.

import { findTilePath } from '../map/pathfinding';
import type { WalkabilityGrid } from '../../shared/types/map';

// ---------------------------------------------------------------------------
// Tunable constants (P7 POL-06 retunes these)
// ---------------------------------------------------------------------------

/** Per-agent minimum ticks between fresh A* computes (500ms at 30 Hz). */
export const P4_PATH_AGENT_THROTTLE_TICKS = 15;

/** Goal-tile cache TTL in ticks (~2s at 30 Hz). */
export const P4_PATH_CACHE_TTL_TICKS = 60;

/** Maximum fresh A* computes per tick across all agents (Threat T-04-08). */
export const P4_PATHS_PER_TICK_BUDGET = 3;

// ---------------------------------------------------------------------------
// Module-level cache state (reset via resetPathingForRound)
// ---------------------------------------------------------------------------

interface PathCacheEntry {
  goalTileKey: string;
  path: Array<[number, number]>;
  computedAtTick: number;
}

const _pathCache = new Map<string, PathCacheEntry>();
const _lastAgentRequestTick = new Map<string, number>();
let _pathsThisTick = 0;
let _currentTick = -1;

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Called once per simulation tick before any requestPath calls.
 * Resets the per-tick path-budget counter and evicts expired cache entries.
 */
export function resetPathingForTick(tick: number): void {
  if (tick !== _currentTick) {
    _currentTick = tick;
    _pathsThisTick = 0;
    // Evict stale entries (Threat T-04-07: unbounded cache growth guard)
    for (const [key, entry] of _pathCache) {
      if (tick - entry.computedAtTick > P4_PATH_CACHE_TTL_TICKS) {
        _pathCache.delete(key);
      }
    }
  }
}

/**
 * Called at round-started. Clears all path caches and per-agent throttle state.
 */
export function resetPathingForRound(): void {
  _pathCache.clear();
  _lastAgentRequestTick.clear();
  _pathsThisTick = 0;
  _currentTick = -1;
}

// ---------------------------------------------------------------------------
// Core API
// ---------------------------------------------------------------------------

/**
 * Request an A* path for an agent. Applies throttle, cache, and per-tick budget.
 *
 * Returns one of:
 *   - { path: [...], status: 'fresh' }        — new A* compute this tick
 *   - { path: [...], status: 'cached' }        — shared from another agent's recent compute
 *   - { path: [],    status: 'stale-throttled' } — throttled or over-budget
 *   - { path: [],    status: 'blocked' }       — goal is unreachable
 */
export function requestPath(
  agentId: string,
  fromTileX: number,
  fromTileY: number,
  goalTileX: number,
  goalTileY: number,
  grid: WalkabilityGrid,
  currentTick: number,
): { path: Array<[number, number]>; status: 'fresh' | 'cached' | 'stale-throttled' | 'blocked' } {
  resetPathingForTick(currentTick);

  const goalKey = `${goalTileX},${goalTileY}`;

  // -- Cache hit / path sharing --
  const cached = _pathCache.get(goalKey);
  if (cached && currentTick - cached.computedAtTick <= P4_PATH_CACHE_TTL_TICKS) {
    const shared = _trimPathToStart(cached.path, fromTileX, fromTileY);
    if (shared.length > 1) {
      return { path: shared, status: 'cached' };
    }
  }

  // -- Per-agent throttle --
  const lastAgentRequest = _lastAgentRequestTick.get(agentId) ?? -Infinity;
  if (currentTick - lastAgentRequest < P4_PATH_AGENT_THROTTLE_TICKS) {
    return { path: [], status: 'stale-throttled' };
  }

  // -- Per-tick global budget (Threat T-04-08) --
  if (_pathsThisTick >= P4_PATHS_PER_TICK_BUDGET) {
    return { path: [], status: 'stale-throttled' };
  }

  // -- Fresh compute --
  const path = findTilePath(fromTileX, fromTileY, goalTileX, goalTileY, grid);
  _pathsThisTick++;
  _lastAgentRequestTick.set(agentId, currentTick);

  if (path.length > 0) {
    _pathCache.set(goalKey, { goalTileKey: goalKey, path, computedAtTick: currentTick });
    return { path, status: 'fresh' };
  }

  return { path: [], status: 'blocked' };
}

/**
 * Returns the A* path length in edges (nodes - 1), or Infinity if unreachable.
 * Does NOT use the throttle/cache; purely computes via findTilePath.
 * Used by isShoutAudible (D-15) and utility scorers.
 */
export function tileGraphDistance(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  grid: WalkabilityGrid,
): number {
  const path = findTilePath(ax, ay, bx, by, grid);
  return path.length > 0 ? path.length - 1 : Infinity;
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Find the best starting index in a cached path closest to (fromX, fromY),
 * then return the sub-path from that index onward.
 *
 * WR-07 fix: reject shared paths when the requesting agent is too far from
 * every waypoint. Previously the "nearest-tile" heuristic could pick an
 * arbitrary mid-path node when the agent was genuinely off-path, yielding a
 * sub-path whose next waypoint required walking BACKWARDS to get to. Callers
 * treat an empty return as "cache miss" and fall through to a fresh compute.
 */
const P4_PATH_SHARING_MAX_DIST_TILES_SQ = 4; // 2 tiles squared: path sharing only when agent is already "on" the path.

function _trimPathToStart(
  path: ReadonlyArray<[number, number]>,
  fromX: number,
  fromY: number,
): Array<[number, number]> {
  let bestIdx = 0;
  let bestDistSq = Infinity;
  for (let i = 0; i < path.length; i++) {
    const [x, y] = path[i]!;
    const dsq = (x - fromX) ** 2 + (y - fromY) ** 2;
    if (dsq < bestDistSq) {
      bestDistSq = dsq;
      bestIdx = i;
    }
  }
  // WR-07: if the agent isn't on/near the cached path, do NOT return a
  // truncation that would walk them backwards toward a far-away waypoint.
  // Empty return signals the caller to fall through to a fresh A* compute.
  if (bestDistSq > P4_PATH_SHARING_MAX_DIST_TILES_SQ) {
    return [];
  }
  return path.slice(bestIdx).map(p => [p[0], p[1]] as [number, number]);
}
